import React, { useEffect } from 'react';
import { useLeadFormStore } from '../../../store/leadFormStore';
import { StepProps } from "../types";

const US_STATES = [
  { value: '', label: 'Select State' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

const AddressStep: React.FC<StepProps> = ({ onNext, onBack }) => {
  const { 
    formData, 
    setAddressData, 
    errors, 
    setError, 
    clearError,
    markStepCompleted,
    saveToStorage 
  } = useLeadFormStore();

  const { address } = formData;

  // Validation function
  const validateField = (field: string, value: string) => {
    switch (field) {
      case 'street':
        if (!value.trim()) {
          setError('street', 'Street address is required');
          return false;
        }
        if (value.length < 5) {
          setError('street', 'Please enter a complete street address');
          return false;
        }
        clearError('street');
        return true;

      case 'city':
        if (!value.trim()) {
          setError('city', 'City is required');
          return false;
        }
        if (value.length < 2) {
          setError('city', 'Please enter a valid city name');
          return false;
        }
        clearError('city');
        return true;

      case 'state':
        if (!value.trim()) {
          setError('state', 'State is required');
          return false;
        }
        clearError('state');
        return true;

      case 'zipCode':
        if (!value.trim()) {
          setError('zipCode', 'ZIP code is required');
          return false;
        }
        const zipRegex = /^\d{5}(-\d{4})?$/;
        if (!zipRegex.test(value)) {
          setError('zipCode', 'Please enter a valid ZIP code');
          return false;
        }
        clearError('zipCode');
        return true;

      default:
        return true;
    }
  };

  // Format ZIP code
  const formatZipCode = (value: string) => {
    const zipCode = value.replace(/[^\d]/g, '');
    if (zipCode.length <= 5) {
      return zipCode;
    }
    return `${zipCode.slice(0, 5)}-${zipCode.slice(5, 9)}`;
  };

  // Handle input changes
  const handleInputChange = (field: string, value: string) => {
    let formattedValue = value;
    
    if (field === 'zipCode') {
      formattedValue = formatZipCode(value);
    }
    
    setAddressData({ ...address, [field]: formattedValue });
    validateField(field, formattedValue);
    saveToStorage();
  };

  // Check if step is completed
  const checkStepCompletion = () => {
    const isValid = 
      validateField('street', address.street) &&
      validateField('city', address.city) &&
      validateField('state', address.state) &&
      validateField('zipCode', address.zipCode);
    
    if (isValid) {
      markStepCompleted('address');
    }
  };

  // Check completion when data changes
  useEffect(() => {
    if (address.street && address.city && address.state && address.zipCode) {
      checkStepCompletion();
    }
  }, [address.street, address.city, address.state, address.zipCode]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900">Address Information</h2>
        <p className="text-sm text-gray-600 mt-1">
          Enter the family's home address
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Street Address */}
        <div>
          <label htmlFor="street" className="block text-sm font-medium text-gray-700">
            Street Address *
          </label>
          <input
            type="text"
            id="street"
            value={address.street}
            onChange={(e) => handleInputChange('street', e.target.value)}
            onBlur={() => validateField('street', address.street)}
            className={`mt-1 block w-full rounded-md shadow-sm ${
              errors.street 
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
            }`}
            placeholder="123 Main Street"
          />
          {errors.street && (
            <p className="mt-1 text-sm text-red-600">{errors.street}</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          {/* City */}
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700">
              City *
            </label>
            <input
              type="text"
              id="city"
              value={address.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              onBlur={() => validateField('city', address.city)}
              className={`mt-1 block w-full rounded-md shadow-sm ${
                errors.city 
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
              }`}
              placeholder="City"
            />
            {errors.city && (
              <p className="mt-1 text-sm text-red-600">{errors.city}</p>
            )}
          </div>

          {/* State */}
          <div>
            <label htmlFor="state" className="block text-sm font-medium text-gray-700">
              State *
            </label>
            <select
              id="state"
              value={address.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              onBlur={() => validateField('state', address.state)}
              className={`mt-1 block w-full rounded-md shadow-sm ${
                errors.state 
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
              }`}
            >
              {US_STATES.map(state => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </select>
            {errors.state && (
              <p className="mt-1 text-sm text-red-600">{errors.state}</p>
            )}
          </div>

          {/* ZIP Code */}
          <div>
            <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">
              ZIP Code *
            </label>
            <input
              type="text"
              id="zipCode"
              value={address.zipCode}
              onChange={(e) => handleInputChange('zipCode', e.target.value)}
              onBlur={() => validateField('zipCode', address.zipCode)}
              className={`mt-1 block w-full rounded-md shadow-sm ${
                errors.zipCode 
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
              }`}
              placeholder="12345"
              maxLength={10}
            />
            {errors.zipCode && (
              <p className="mt-1 text-sm text-red-600">{errors.zipCode}</p>
            )}
          </div>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="mt-6 p-4 bg-blue-50 rounded-md">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Step 2.3 Status:</h3>
        <div className="text-xs text-blue-600">
          ✅ Address form with validation and formatting working
        </div>
        <div className="text-xs text-blue-500 mt-1">
          Complete all address fields to proceed to children step
        </div>
      </div>
    </div>
  );
};

export default AddressStep;
